import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import Loader from '~/app/common/Loader';
import Icon from '~/app/common/Icon';
import { REQUESTS_ROUTES } from '~/notifications/Router';
import { useAppHeader } from '~/app/modules/appHeader';
import Messages from '~/messages';
import classes from './style.module.scss';
import { MessageType } from '~/app/data/statuses/chat';
import type { ProjectChatDetailApi } from '~/projects/store/project';

function isProjectActive(status: ProjectChatDetailApi['status']): boolean {
    if (status == null) return false;
    if (status === false || status === 0) return false;
    return true;
}

const ProjectDetail: React.FC = () => {
    const history = useHistory();
    const { projectId } = useParams<{ projectId: string }>();
    const { setData, clearData } = useAppHeader();
    const { t } = useTranslation('issue.messages');
    const { t: tIssue } = useTranslation('issue');
    const { t: tChat } = useTranslation('chat');
    const netWorkStatus = useStoreState((s) => s.app.offline.status);

    const loading = useStoreState((s) => s.project.projectChatLoading);
    const detail = useStoreState((s) => s.project.projectChatDetail);

    const loadProjectChat = useStoreActions((a) => a.project.loadProjectChat);
    const resetProjectChat = useStoreActions((a) => a.project.resetProjectChat);

    const active = detail != null && isProjectActive(detail.status);

    const headerTitle = useMemo(() => {
        if (detail?.number != null && detail.number !== '') {
            return t('projectChatHeaderTitle', { number: detail.number });
        }
        return tChat('projectChat');
    }, [detail?.number, t, tChat]);

    const pageTitle = useMemo(() => {
        if (detail?.number && detail?.name) {
            return `${detail.number} — ${detail.name}`;
        }
        return tChat('projectChat');
    }, [detail?.number, detail?.name, tChat]);

    const metaLine = useMemo(() => {
        if (!detail) return '';
        const parts: string[] = [];
        if (detail.number) {
            parts.push(t('projectChatHeaderTitle', { number: detail.number }));
        }
        if (detail.updated_at) {
            parts.push(t('projectLastUpdated', { date: detail.updated_at }));
        } else if (detail.created_at) {
            parts.push(t('projectCreated', { date: detail.created_at }));
        }
        return parts.join(' · ');
    }, [detail, t]);

    usePageTitle(pageTitle);

    useEffect(() => {
        if (netWorkStatus !== 0) {
            history.push(ROUTES.projects);
        }
    }, [netWorkStatus, history]);

    useEffect(() => {
        if (!projectId) return;
        loadProjectChat(projectId).catch(() => {});
        return () => resetProjectChat();
    }, [projectId, loadProjectChat, resetProjectChat]);

    useEffect(() => {
        setData({
            title: headerTitle,
            subTitle: detail?.name || '',
            backLink:
                history.length > 1
                    ? history.goBack
                    : () => history.push(REQUESTS_ROUTES.chat),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false,
        });
        return () => clearData();
    }, [setData, clearData, headerTitle, detail?.name, history]);

    if (!projectId) return null;

    if (loading && !detail) {
        return (
            <div className={classes['loader-wrap']}>
                <Loader size="32" />
            </div>
        );
    }

    return (
        <div className={classes.page}>
            {netWorkStatus === 0 && detail !== null ? (
                <>
                    <div
                        className={`${classes['status-banner']} ${active ? classes['banner-active'] : classes['banner-closed']}`}
                    >
                        <Icon name={active ? 'schedule' : 'lock_outline'} className={classes['banner-icon']} />
                        <span>{active ? t('projectChatMetaActive') : t('projectChatMetaClosed')}</span>
                    </div>

                    <div className={classes['detail-block']}>
                        <div className={classes['primary-title']}>{detail.name || '—'}</div>
                        {metaLine ? <div className={classes['meta-line']}>{metaLine}</div> : null}

                        <div className={classes['info-wrapper']}>
                            <div className={classes['field-label']}>{tIssue('statusTitle')}</div>
                            <div className={classes['field-value']}>
                                <Icon
                                    name="stop_circle"
                                    className={`${classes['status-icon']} ${active ? classes['status-open'] : classes['status-closed']}`}
                                />
                                {active ? t('projectStatusOpen') : t('projectStatusClosed')}
                            </div>
                        </div>

                        <div className={classes['info-wrapper']}>
                            <div className={classes['field-label']}>{t('projectStartDate')}</div>
                            <div className={classes['field-value']}>{detail.startDate ?? '—'}</div>
                        </div>

                        <div className={classes['info-wrapper']}>
                            <div className={classes['field-label']}>{t('projectEndDate')}</div>
                            <div className={classes['field-value']}>{detail.endDate ?? '—'}</div>
                        </div>

                        <div className={classes['info-wrapper']}>
                            <div className={classes['field-label']}>{t('projectStandardPriceType')}</div>
                            <div className={classes['field-value']}>{detail.standardPriceType ?? '—'}</div>
                        </div>

                        <div className={classes['info-wrapper']}>
                            <div className={classes['field-label']}>{t('projectWorkingDays')}</div>
                            <div className={classes['field-value']}>{detail.workingDays ?? '—'}</div>
                        </div>

                        <hr className={classes['bottom-line']} />
                    </div>
                </>
            ) : null}

            <Messages type={MessageType.PROJECT} issue={detail ?? undefined} />
        </div>
    );
};

export default ProjectDetail;
